import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { api } from "@/lib/api";

const t = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" }) : "—");
const d = (ts) => (ts ? new Date(ts).toLocaleString() : "—");
const SRC_CLASS = (s) => (s?.startsWith("OWNER") || ["UPLOAD_INGEST", "CREATOR_SAVE", "ADMIN_SAVE"].includes(s) ? "text-green-300" : s?.startsWith("AI") ? "text-sky-300" : "text-amber-300");

const Diff = ({ label, diff }) => {
  if (!diff || (!diff.added?.length && !diff.removed?.length)) return null;
  return (
    <div data-testid={`history-diff-${label}`}>
      <span className="opacity-70">{label}</span>{" "}
      {diff.added?.map((x) => <span key={"+" + x} className="text-green-400 mr-1.5">+ {x}</span>)}
      {diff.removed?.map((x) => <span key={"-" + x} className="text-red-300 mr-1.5">− {x}</span>)}
    </div>
  );
};

const Event = ({ ev, i }) => {
  const md = ev.diff?.manual_tags || {};
  const manualChanged = md.added?.length || md.removed?.length;
  return (
    <li data-testid={`tag-history-event-${i}`} className={`rounded border p-1.5 bg-black/20 ${ev.violation ? "border-red-500/60" : "border-border/60"}`}>
      <div><span className="text-foreground/90">{t(ev.ts)}</span> — <span className={`font-semibold ${SRC_CLASS(ev.source)}`} data-testid={`tag-history-source-${i}`}>{ev.source}</span>
        <span className="opacity-60"> · {ev.user_id || "no user"} · {ev.endpoint || ev.action?.split(":")[0]} · req {ev.request_id || ev.op_id}</span></div>
      {ev.violation && <div className="text-red-300" data-testid="tag-history-violation">⚠ {ev.violation}</div>}
      <Diff label="manual_tags" diff={md} />
      <Diff label="ai_tags" diff={ev.diff?.ai_tags} />
      <Diff label="owner_rejected_tags" diff={ev.diff?.owner_rejected_tags} />
      <Diff label="effective_tags" diff={ev.diff?.effective_tags} />
      {manualChanged ? (
        <div className="opacity-70 break-all">manual_tags before [{(ev.before?.manual_tags || []).join(", ")}] → after [{(ev.after?.manual_tags || []).join(", ")}]</div>
      ) : (ev.diff?.manual_tags || ev.before?.manual_tags) ? <div className="opacity-60">manual_tags unchanged.</div> : null}
      {ev.action && <div className="opacity-70">{ev.action}</div>}
    </li>
  );
};

export const TagHistoryPanel = ({ mediaId, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { setData(null); }, [mediaId]);
  useEffect(() => {
    if (!open || !mediaId) return;
    api.get(`/admin/media/${mediaId}/tag-history`).then((r) => setData(r.data)).catch(() => setData({ events: [], provenance: [] }));
  }, [open, mediaId, refreshKey]);
  return (
    <details className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/5 p-2.5" data-testid="tag-history-panel" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="text-[10px] uppercase tracking-widest text-sky-300 cursor-pointer flex items-center gap-1"><History className="w-3 h-3" /> Tag history — admin only {data ? `(${data.event_count} events)` : ""}</summary>
      {!data ? <div className="text-[11px] text-muted-foreground mt-1">Loading…</div> : (
        <div className="mt-2 text-[11px] font-mono space-y-3">
          <div>
            <div className="font-semibold mb-1">Current tag provenance</div>
            <table className="w-full text-left" data-testid="tag-provenance-table">
              <thead><tr className="opacity-60"><th className="pr-2">Tag</th><th className="pr-2">Source</th><th className="pr-2">Added at</th><th className="pr-2">Added by</th><th>Last writer</th></tr></thead>
              <tbody>
                {(data.provenance || []).map((p) => (
                  <tr key={p.tag} data-testid={`tag-provenance-${p.tag.replace(/\s+/g, "-")}`} className={p.rejected ? "opacity-50 line-through" : ""}>
                    <td className="pr-2">{p.tag}</td>
                    <td className={`pr-2 ${p.source === "manual" ? "text-green-300" : p.source === "AI" ? "text-sky-300" : "text-amber-300"}`}>{p.source}</td>
                    <td className="pr-2">{p.added_at ? d(p.added_at) : "—"}</td>
                    <td className={`pr-2 ${p.added_by === "legacy / source unknown" ? "text-muted-foreground italic" : SRC_CLASS(p.added_by)}`}>{p.added_by}</td>
                    <td className={p.last_writer === "legacy / source unknown" ? "text-muted-foreground italic" : SRC_CLASS(p.last_writer)}>{p.last_writer}</td>
                  </tr>
                ))}
                {(data.provenance || []).length === 0 && <tr><td colSpan={5} className="text-muted-foreground">no effective tags</td></tr>}
              </tbody>
            </table>
            <div className="opacity-60 mt-1">manual = you chose it · AI = ai_tags (never merged into manual) · system / upload default = machine facts · struck-through = in owner_rejected_tags</div>
          </div>
          {data.violations?.length > 0 && <div className="text-red-300" data-testid="tag-history-violations">⚠ {data.violations.length} manual_tags write(s) by a non-owner source — see red events below</div>}
          <div>
            <div className="font-semibold mb-1">Events (newest first) · immutable log: {data.immutable_log_events}</div>
            {(data.events || []).length === 0 ? <div className="text-muted-foreground" data-testid="tag-history-empty">No tag-write events recorded for this media (tags predate the audit trail → legacy / source unknown).</div> : (
              <ol className="space-y-1.5" data-testid="tag-history-list">{data.events.map((ev, i) => <Event key={(ev.op_id || "") + i} ev={ev} i={i} />)}</ol>
            )}
          </div>
        </div>
      )}
    </details>
  );
};
